import { Button } from "@govlaunch/core";
import * as palette from "@govlaunch/palette";
import Popover from "@govlaunch/popover";
import difference from "lodash/fp/difference";
import React from "react";
import { FormProps } from "react-final-form";
import { useToggle } from "react-use";
import { ITag } from "~/types/types";
import { NoAwards } from "~/components/profile/EmptyBlocks";
import Award from "~/components/awards/Award";
import AwardsForm from "~/components/awards/AwardsForm";
import { FormattedMessage } from "react-intl";

interface IGroupAwardsProps {
  awards: Pick<ITag, "_id" | "name">[];
  onSubmit: FormProps["onSubmit"];
  onAddLink: () => void;
  readOnly?: boolean;
}

export default function AwardsBox({
  awards,
  onSubmit,
  onAddLink,
  readOnly = false,
  ...props
}: IGroupAwardsProps & FormProps) {
  const [isEditing, toggle] = useToggle(false);
  const [isTooltipOpen, toggleTooltip] = useToggle(false);

  if (readOnly && awards.length === 0) {
    return null;
  }

  if (awards.length === 0 && !isEditing) {
    return <NoAwards onAddAwards={toggle} />;
  }

  return (
    <Popover
      isOpen={isTooltipOpen}
      onClickOutside={toggleTooltip}
      placement="right"
      render={() => (
        <div
          css={{
            width: 275,
            padding: 16,
          }}
        >
          <div
            css={{
              paddingBottom: 8,
              borderBottom: `1px solid ${palette.lightestGray}`,
              color: palette.mediumGray,
            }}
          >
            <FormattedMessage defaultMessage="Do you want to add a link about the award?" id="epDw9F" />
          </div>
          <div
            css={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              marginTop: 8,
            }}
          >
            <Button
              size="xsmall"
              color={palette.primary}
              type="button"
              onClick={() => {
                onAddLink();
                toggleTooltip();
              }}
            >
              <FormattedMessage defaultMessage="Yes" id="a5msuh" />
            </Button>

            <Button
              size="xsmall"
              theme="secondary"
              onClick={toggleTooltip}
              color={palette.primary}
              css={{
                marginLeft: 8,
              }}
            >
              <FormattedMessage defaultMessage="No" id="oUWADl" />
            </Button>
          </div>
        </div>
      )}
    >
      {({ innerRef }: any) => (
        <div
          ref={innerRef}
          css={{
            background: "#fff",
            borderRadius: 4,
            border: `1px solid ${palette.lightestGray}`,
          }}
        >
          <div
            css={{
              padding: 16,
              display: "flex",
              borderBottom: `1px solid ${palette.lightestGray}`,
            }}
          >
            <span
              css={{
                fontSize: 12,
                fontWeight: 500,
                color: palette.sealBlue,
                textTransform: "uppercase",
              }}
            >
              <FormattedMessage defaultMessage="🏅 Awards" id="RT1ZpG" />
            </span>

            {awards.length > 0 && !isEditing && !readOnly && (
              <span
                role="button"
                css={{
                  marginLeft: "auto",
                  fontWeight: 500,
                  textTransform: "uppercase",
                  color: palette.primary,
                  cursor: "pointer",
                  fontSize: 14,
                }}
                onClick={toggle}
              >
                <FormattedMessage defaultMessage="Edit" id="wEQDC6" />
              </span>
            )}
          </div>

          <div
            css={{
              padding: 16,
            }}
          >
            {!isEditing && awards.length > 0 && (
              <div
                css={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  marginTop: -8,
                  // something is strange with the way this component is built and if a maxWidth is not set
                  // and the Award itself becomes too wide it messes the whole page layout
                  maxWidth: 320,
                }}
              >
                {awards.map((award) => {
                  return <Award key={award._id} award={award} />;
                })}
              </div>
            )}

            {isEditing && (
              <AwardsForm
                onCancel={toggle}
                initialValues={{
                  awards,
                }}
                onSubmit={(values, form) => {
                  const hasDifferentAwards =
                    difference(
                      values.awards.map(({ _id, objectID }: any) => _id || objectID),
                      awards.map(({ _id }) => _id),
                    ).length > 0;

                  return Promise.resolve(onSubmit(values, form))
                    .then(toggle)
                    .then(() => {
                      if (values.awards.length > 0 && hasDifferentAwards) {
                        toggleTooltip();
                      }
                    });
                }}
                {...props}
              />
            )}
          </div>
        </div>
      )}
    </Popover>
  );
}
